import { API_CONFIG } from '../config/api-config';
import { ApiClientError } from './types';
/**
 * Generic API Client with authentication and retry logic
 * Packages construct their own full URLs using this client
 */
export class ApiClient {
    constructor(baseUrl, options = {}) {
        this.baseUrl = baseUrl;
        this.defaultOptions = {
            timeout: API_CONFIG.DEFAULTS.timeout,
            retries: API_CONFIG.DEFAULTS.retries,
            headers: {
                ...API_CONFIG.DEFAULTS.headers,
                ...options.headers
            },
            ...options
        };
    }
    /**
     * Set callback for when 401 Unauthorized response is received
     */
    setUnauthorizedCallback(callback) {
        this.onUnauthorized = callback;
    }
    /**
     * Get authentication headers
     */
    getAuthHeaders() {
        const token = localStorage.getItem(API_CONFIG.AUTH.tokenKey);
        const headers = {};
        if (token) {
            headers[API_CONFIG.AUTH.tokenHeader] = `${API_CONFIG.AUTH.tokenPrefix} ${token}`;
        }
        return headers;
    }
    /**
     * Make HTTP request with retry logic and error handling
     */
    async makeRequest(endpoint, config = {}) {
        const { method = 'GET', headers: configHeaders = {}, body, timeout = this.defaultOptions.timeout, retries = this.defaultOptions.retries } = config;
        const url = endpoint.startsWith('http') ? endpoint : `${this.baseUrl}${endpoint}`;
        // Check if body is FormData
        const isFormData = body instanceof FormData;
        // Don't set Content-Type for FormData (browser will set it with boundary)
        const finalHeaders = isFormData
            ? {
                ...this.getAuthHeaders(),
                ...configHeaders
            }
            : {
                'Content-Type': 'application/json',
                ...this.getAuthHeaders(),
                ...configHeaders
            };
        let lastError = null;
        for (let attempt = 0; attempt <= retries; attempt++) {
            try {
                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), timeout);
                const response = await fetch(url, {
                    method,
                    headers: finalHeaders,
                    body: isFormData ? body : (body ? JSON.stringify(body) : undefined),
                    signal: controller.signal,
                });
                clearTimeout(timeoutId);
                if (!response.ok) {
                    // Handle 401 Unauthorized - trigger logout (but not for logout endpoint itself)
                    if (response.status === 401 && this.onUnauthorized && !endpoint.includes('/auth/logout')) {
                        this.onUnauthorized();
                    }
                    let errorMessage = response.statusText;
                    let errorData = null;
                    try {
                        errorData = await response.json();
                        // Prioritize the API response message
                        errorMessage = errorData.message || errorData.error || errorMessage;
                    }
                    catch {
                        // Use default error message if JSON parsing fails
                    }
                    throw new ApiClientError(errorMessage, response.status, response, errorData);
                }
                // Handle response
                const contentType = response.headers.get('content-type');
                if (contentType?.includes('application/json')) {
                    return await response.json();
                }
                else {
                    return (await response.text());
                }
            }
            catch (error) {
                lastError = error instanceof Error ? error : new Error(String(error));
                // Don't retry on 4xx errors (except 408 timeout)
                if (error instanceof ApiClientError && error.status >= 400 && error.status < 500 && error.status !== 408) {
                    break;
                }
                // TEMPORARILY DISABLED: Don't retry on 5xx errors for easier debugging
                if (error instanceof ApiClientError && error.status >= 500) {
                    break;
                }
                if (attempt < retries && this.isRetryableError(error)) {
                    // Wait before retry (exponential backoff)
                    await this.delay(Math.pow(2, attempt) * 1000);
                    continue;
                }
                break;
            }
        }
        throw lastError;
    }
    /**
     * Check if error is retryable
     */
    isRetryableError(error) {
        if (error instanceof ApiClientError) {
            // Retry on server errors (5xx) and timeout (408)
            return (error.status >= 500 && error.status < 600) || error.status === 408;
        }
        // Retry on network errors, timeout errors, etc.
        return error.name === 'AbortError' || error.name === 'TypeError';
    }
    /**
     * Delay utility for retry logic
     */
    delay(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
    /**
     * HTTP Methods
     */
    async get(endpoint, config) {
        return this.makeRequest(endpoint, { ...config, method: 'GET' });
    }
    async post(endpoint, body, config) {
        return this.makeRequest(endpoint, { ...config, method: 'POST', body });
    }
    async put(endpoint, body, config) {
        return this.makeRequest(endpoint, { ...config, method: 'PUT', body });
    }
    async delete(endpoint, config) {
        return this.makeRequest(endpoint, { ...config, method: 'DELETE' });
    }
    async multiDelete(endpoint, ids, config) {
        return this.makeRequest(endpoint, { ...config, method: 'DELETE', body: { ids } });
    }
    async patch(endpoint, body, config) {
        return this.makeRequest(endpoint, { ...config, method: 'PATCH', body });
    }
    /**
     * Authentication Methods
     */
    setAuthToken(token) {
        this.defaultOptions.headers = this.defaultOptions.headers || {};
        this.defaultOptions.headers[API_CONFIG.AUTH.tokenHeader] = `${API_CONFIG.AUTH.tokenPrefix} ${token}`;
    }
    clearAuthToken() {
        if (this.defaultOptions.headers) {
            delete this.defaultOptions.headers[API_CONFIG.AUTH.tokenHeader];
        }
    }
    setOnUnauthorized(callback) {
        this.onUnauthorized = callback;
    }
}
/**
 * Factory function to create API client with base configuration
 */
export const createApiClient = (baseUrl, options) => {
    return new ApiClient(baseUrl, options);
};
//# sourceMappingURL=client.js.map